import { focusCalendarAgenda } from '../actions/focusCalendarAgenda';
import { IconButton } from '@fluentui/react/lib/Button';
import { IPanelStyles, Panel } from '@fluentui/react/lib/Panel';
import { getFirstFocusable } from '@fluentui/react/lib/Utilities';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { AgendaView } from 'owa-calendar-agenda';
import { canShowAgendaPane, getSelectedDateDateRange } from 'owa-calendar-surface-surface-store';
import { ControlIcons } from 'owa-control-icons';
import loc from 'owa-localize';
import { useOrchestrator } from 'owa-react-hooks/lib/useOrchestrator';
import SkipLinkRegionToFirstFocusableChild from 'owa-skip-link/lib/components/SkipLinkRegionToFirstFocusableChild';
import { isShySuiteHeaderMode } from 'owa-suite-header-store';
import * as React from 'react';
import {
    calendarAgendaToggle,
    collapseAgendaPaneTitle,
    expandAgendaPaneTitle,
    agendaViewRegionName,
    agendaViewToggleRegionName,
} from 'owa-calendar-shared-strings/lib/strings/calendarAgenda.locstring.json';
import {
    shouldShowAgendaPaneAsOverlay,
    getIsAgendaPaneShown,
    toggleAgendaPane,
} from 'owa-calendar-layout';

const styles = require('./CalendarAgenda.scss');
const overlayStyles = require('owa-calendar-layout/lib/SidePaneOverlay.scss');
const agendaViewWidth = '320px';

export default observer(function CalendarAgenda() {
    const paneContainer = React.useRef<HTMLDivElement>();

    useOrchestrator(
        focusCalendarAgenda,
        () => {
            if (paneContainer.current) {
                getFirstFocusable(
                    paneContainer.current,
                    paneContainer.current,
                    true /* includeElementsInFocusZones */
                )?.focus();
            }
        },
        []
    );

    if (!canShowAgendaPane()) {
        return null;
    }
    const agendaPaneShown = getIsAgendaPaneShown();
    const regionNameId = agendaPaneShown ? agendaViewRegionName : agendaViewToggleRegionName;
    const agendaPaneProps: Partial<IPanelStyles> = {
        main: {
            width: agendaViewWidth,
        },
        contentInner: {
            height: '100%',
        },
        scrollableContent: {
            height: '100%',
            overflow: 'hidden',
        },
        content: {
            padding: 0,
            height: '100%',
        },
        commands: {
            margin: 0,
            height: 1,
        },
    };
    const agendaPaneContent = getAgendaContent();
    return (
        <SkipLinkRegionToFirstFocusableChild
            role="complementary"
            regionNameResourceId={regionNameId}
            ref={paneContainer}
            className={styles.agendaPane}>
            {shouldShowAgendaPaneAsOverlay() ? (
                getIsAgendaPaneShown() && (
                    // checking isAgendaPaneShown() on each component since the toggle button needs to be in the
                    // addSkipLinkRegionToFirstFocusableChild for accessibility focus concerns
                    <Panel
                        className={classNames(overlayStyles.overlayPane, {
                            isShyHeaderMode: isShySuiteHeaderMode(),
                        })}
                        isLightDismiss={true}
                        isOpen={agendaPaneShown}
                        hasCloseButton={false}
                        focusTrapZoneProps={{
                            forceFocusInsideTrap: true,
                            isClickableOutsideFocusTrap: true,
                        }}
                        onDismiss={toggleAgendaPane}
                        overlayProps={{
                            isDarkThemed: true,
                        }}
                        styles={agendaPaneProps}>
                        {agendaPaneShown && (
                            <div className={styles.agendaContent}>{agendaPaneContent}</div>
                        )}
                    </Panel>
                )
            ) : (
                <div className={agendaPaneShown ? styles.agendaPaneNonOverlay : undefined}>
                    {agendaPaneShown && (
                        <div className={styles.agendaContent}>{agendaPaneContent}</div>
                    )}
                </div>
            )}

            <div className={agendaPaneShown ? styles.footerContainer : undefined}>
                {
                    <IconButton
                        title={
                            agendaPaneShown
                                ? loc(collapseAgendaPaneTitle)
                                : loc(expandAgendaPaneTitle)
                        }
                        className={
                            !agendaPaneShown ? styles.openAgendaPaneIconRoot : styles.iconRoot
                        }
                        styles={{ icon: styles.icon }}
                        iconProps={{
                            iconName: agendaPaneShown
                                ? ControlIcons.ClosePane
                                : ControlIcons.OpenPane,
                        }}
                        onClick={toggleAgendaPane}
                        role="button"
                        aria-expanded={agendaPaneShown}
                        aria-label={loc(calendarAgendaToggle)}
                    />
                }
            </div>
        </SkipLinkRegionToFirstFocusableChild>
    );
});

function getAgendaContent() {
    return <AgendaView date={getSelectedDateDateRange().start} />;
}
